<template>
  <Auth title="Login To Your Account">
    <main>
      <!-- Tenant Setup Screen -->
      <transition name="fade">
        <div v-if="tenantSetupStatus" class="flex flex-col items-center justify-center">
          <!-- Title -->
          <h2 class="text-3xl font-bold text-gray-900 mb-4">Setting Up Your Institute</h2>

          <!-- Status Message -->
          <p class="text-lg text-gray-700 mb-8">{{ tenantSetupMessage }}</p>

          <!-- Progress Dots -->
          <div v-if="showTenantSetupLoader">
            <Loader class="mt-20" :content="true" />
          </div>

          <!-- Success State -->
          <div v-if="tenantSetupStatus === 'completed'" class="text-center">
            <div
              class="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4"
            >
              <svg
                class="w-10 h-10 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <p class="text-green-600 font-semibold text-lg">Setup Complete!</p>
          </div>

          <!-- Failed State -->
          <div v-if="tenantSetupStatus === 'failed'" class="text-center">
            <div
              class="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4"
            >
              <svg
                class="w-10 h-10 text-red-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>
            <p class="text-red-600 font-semibold text-lg">Setup Failed</p>
          </div>
        </div>
      </transition>
      <!-- Show login form only when setup is not in progress -->
      <template v-if="!tenantSetupStatus">
        <Loader v-if="isOtpAutoLoginInProgress" class="mt-20 mb-40" :content="true" />
        <div v-else>
          <ThirdPartySignin
            :remember-me="loginData.remember_me"
            @google-login="handleGoogleLogin"
            @microsoft-login="handleMicrosoftLogin"
          />

          <Loader v-if="loginProvider" class="mt-20" :content="true" />
          <template v-else>
            <ValidationObserver v-slot="{ handleSubmit }">
              <UIErrorNotification
                v-if="errorMsg"
                class="mt-5"
                :text="errorMsg"
                :is-resend-button="isResetButton"
                @resendEmail="resendRegistrationEmailHandler"
              />
              <div
                class="mb-3 font-inter lg:text-lg sm:text-base text-sm text-primary-purple-700 flex justify-end font-medium"
              >
                <!-- Temporarily disabled because you can't log in with your phone number. -->
                <!-- <span class="cursor-pointer" @click="switchLoginWith">
            {{ loginButtonText }}
          </span> -->
              </div>
              <UiInput
                v-if="loginWithEmail"
                id="email"
                v-model.trim="loginData.email"
                type="email"
                name="email"
                rules="required|email"
                :value="loginData.email"
                placeholder="Email"
                title="email"
                @keyup.enter="!isGoogleLogin && handleSubmit(loginHandler)"
                @input="onInputUserEmail"
              />
              <PhoneNumber
                v-else
                v-model="phoneNumber"
                :rules="!loginData.email ? 'required|validNumber' : 'validNumber'"
                title="Phone Number"
                @update="countryChanged"
              />
              <div v-if="isDomainSelectionActive" class="relative">
                <UiSingleSelect
                  v-model.trim="loginData.sub_domain"
                  title="SUB_DOMAIN"
                  placeholder="Institute Sub-Domain"
                  label="title"
                  :options="userDomains"
                  :tooltip="true"
                  :tooltip-description="tooltipDescription"
                  rules="required"
                />
                <div
                  class="absolute top-9 right-2.5 rtl:right-auto rtl:left-2.5 pointer-events-none flex justify-end items-center bg-transparent font-inter text-primary-gray-500 font-medium px-4"
                >
                  <div class="text-sm xl:text-base pt-0.5 xl:pt-0 domain-text">
                    {{ hostName }}
                  </div>
                </div>
              </div>
              <UiInput
                id="password"
                v-model.trim="loginData.password"
                :class="errorMsg ? '' : ''"
                class="relative"
                type="password"
                name="password"
                rules="required"
                placeholder="Password"
                :value="loginData.password"
                title="Password"
                @keyup.enter="!isGoogleLogin && handleSubmit(loginHandler)"
              />
              <div class="flex justify-between items-center mb-6">
                <div>
                  <UiCheckbox
                    v-model="loginData.remember_me"
                    label="Remember Me"
                    size="sm"
                    lable-direction="flex-row-reverse gap-2"
                  />
                </div>

                <div class="forgot-Password flex justify-end">
                  <router-link
                    to="/user/forget-password"
                    class="font-inter font-semibold text-sm text-primary-purple-700"
                  >
                    <a>
                      <span v-i18n="loginPage">Forgot Password ?</span>
                    </a>
                  </router-link>
                </div>
              </div>
              <UIButton
                button="primary"
                :disabled="isLoading"
                class="w-full h-8 sm:h-12"
                type="button"
                @click="!isGoogleLogin && handleSubmit(loginHandler)"
              >
                <span v-if="!isGoogleLogin && isLoading">
                  <Loader class="mt-20" />
                </span>

                <span v-else v-i18n="loginPage">Log In</span>
              </UIButton>

              <p
                v-if="isDomainSelectionActive"
                class="mt-8 font-inter text-sm flex justify-center gap-1"
              >
                <span v-i18n="loginPage" class="font-normal text-primary-gray-500">
                  SIGNUP_INFO
                </span>
                <span
                  v-i18n="loginPage"
                  class="font-semibold text-primary-purple-700 cursor-pointer"
                  @click="redirectToSignup"
                >
                  GET_STARTED
                </span>
              </p>
            </ValidationObserver>
          </template>
        </div>
      </template>
    </main>
  </Auth>
</template>

<script>
const DELAY_FOR_CHECK_USER_REGISTRATION = 1500

import { objectDeepCopy } from '@utils/generalUtil'
import { redirectToSignup } from '@src/utils/generalUtil.js'
import { removeEmptyKeysFromObject, getDefaultRoute } from '@src/utils/generalUtil.js'
import { siteDomainHandler } from '@src/utils/site-utils.js'
import { handleNotificationPermission } from '@src/utils/notification-util.js'
import { DOMAIN_TITLE } from '@src/constants/general-constants'
import UiInput from '@components/UiElements/UiInputBox.vue'
import UiCheckbox from '@components/UiElements/UiCheckbox.vue'
import Auth from '@layouts/auth.vue'
import UIErrorNotification from '@components/UiElements/UIErrorNotification.vue'
import Loader from '@components/BaseComponent/Loader.vue'
import PhoneNumber from '@components/UiElements/PhoneNumber.vue'
import UIButton from '@components/UiElements/UIButton.vue'
import themeAndLangMixin from '@src/mixins/theme-and-lang-mixins'
import { TENANT_ROLES } from '@src/constants/user-roles-constants.js'
import initializePushNotifications from '@src/push-notifications'
import { mapState, mapActions, mapGetters } from 'vuex'
import { Form as ValidationObserver } from 'vee-validate'
import debounce from 'lodash/debounce'
import { isValidEmail } from '@src/vee-validate/index.js'
import {
  currentDomainType,
  siteConstants,
  isLocalHost,
  isInHouseDevServers,
} from '@src/utils/site-utils.js'
import ThirdPartySignin from './ThirdPartySignin.vue'
import UiSingleSelect from '@/src/components/UiElements/UiSingleSelect.vue'
import { isTawkToAllowed, showTawkWidget, hideTawkWidget } from '@src/utils/widgetUtil.js'
import { isCapacitor } from '@src/utils/platformUtil.js'
import {
  getTenantSetupStatusFromPayload,
  shouldConnectTenantSetupSocket,
  getTenantSetupSocketHandlers,
} from '@src/utils/auth.util.js'
export default {
  name: 'SignInPage',
  components: {
    Loader,
    UiInput,
    UiCheckbox,
    Auth,
    ValidationObserver,
    UIErrorNotification,
    PhoneNumber,
    UIButton,
    UiSingleSelect,
    ThirdPartySignin,
    Loader,
  },
  mixins: [themeAndLangMixin],
  data() {
    return {
      TENANT_ROLES,
      siteConstants,
      isLoading: false,
      showText: false,
      isGoogleLogin: false,
      errorMsg: null,
      otpData: {},
      loginData: {
        password: '',
        sub_domain: '',
        remember_me: false,
      },
      userDomains: [],
      tooltipDescription: 'Your custom web address prefix (e.g., schoolname.myqampus.com)',
      googleIcon: 'https://www.svgrepo.com/show/475656/google-color.svg',
      error: '',
      isDomainSelectionActive: true,
      loginPage: 'loginPage',
      phoneNumber: '',
      phoneNumberValid: false,
      loginWithEmail: true,
      instituteName: '',
      isResetButton: false,
      currentHostName: window.location.hostname,
      isOtpAutoLoginInProgress: false,
      otpLoginRetryCount: 0,
      tenantSetupStatus: null,
      tenantSetupMessage: '',
      tenantOtp: null,
      tenantToken: null,
      tenantEmail: null,
      tenantSetup: null,
      tenantSubDomain: null,
      loginProvider: null,
    }
  },
  page: {
    title: 'Signin',
    meta: [{ name: 'description', content: 'Sign in MyQampus' }],
  },
  computed: {
    loginButtonText() {
      return this.loginWithEmail ? 'Sign In with Phone' : 'Sign In with Email/Username'
    },
    ...mapGetters('auth', ['userRoles', 'currentUser']),
    ...mapState({
      systemSettings: (state) => state.settings.siteSetting,
      currentCampusScope: (state) => state.layout.currentCampusScope,
      currentSectionScope: (state) => state.layout.currentSectionScope,
      activeRole: (state) => state.layout.activeRole,
    }),
    isCapacitor() {
      return isCapacitor()
    },

    hostName() {
      if (this.isCapacitor) return ''

      return this.currentHostName.includes('app')
        ? '.' + this.currentHostName.slice(4)
        : '.' + this.currentHostName
    },
    siteDomain() {
      return siteDomainHandler().domain
    },

    pageDescription() {
      return `Welcome to ${this.instituteName}. Where you can automate your routine institute related work.`
    },
    isOnTenantDomain() {
      return !this.currentHostName.includes('app.')
    },
    showTenantSetupLoader() {
      return (
        this.tenantSetupStatus !== 'completed' &&
        this.tenantSetupStatus !== 'failed' &&
        this.tenantSetupStatus !== 'connecting'
      )
    },
  },
  /*
   * In created hook
   * Check if the user allows notification or not if not show toast message
   * Check if user surf site from domain then hide domain field and take domain from host eg.demo.dev.myqampus.com
   */
  created() {
    this.checkOtpQueryAndLogin()
    this.notificationPermissionHandler()
    this.tenantOtp = this.$route.query.otp
    this.tenantEmail = this.$route.query.email
    this.tenantSetup = this.$route.query.setup
    this.tenantSubDomain = this.$route.query.sub_domain
    this.loginProvider = this.$route?.query?.provider
  },
  mounted() {
    this.domainHandler()
    this.setTawkAttributes()
    this.hideTawkWidgetOnBeforeLoad()
    this.connectTenantSetupSocket()
  },
  beforeUnmount() {
    // Cleanup socket connection
    if (this.$publicSocket) {
      this.$publicSocket.disconnect()
    }
  },
  methods: {
    isLocalHost,
    setTawkAttributes() {
      if (!window?.Tawk_API) return
      const appUrl = window.location.origin
      const attributes = {
        name: this.currentUser?.full_name || '',
        email: this.currentUser?.email || '',
        institute: this.$store?.state?.settings?.generalSettingInstituteName,
        tenantUrl: appUrl,
        userId: this.currentUser?.id || '',
      }
      try {
        if (window?.Tawk_API?.setAttributes) {
          window.Tawk_API.setAttributes(attributes, () => {})
        } else {
          return
        }
      } catch (error) {
        this.$store.commit('toast/NEW', { type: 'error', message: error.message }, { root: true })
      }
    },
    hideTawkWidgetOnBeforeLoad() {
      if (window.Tawk_API)
        window.Tawk_API.onBeforeLoad = () => {
          hideTawkWidget()
        }
    },
    checkOtpQueryAndLogin() {
      const otp_code = this.$route.query.otp_code
      const sub_domain = this.$route.query.sub_domain
      const user_id = this.$route.query.user_id
      const remember_me = this.$route.query.remember_me

      if (otp_code && sub_domain && user_id) {
        this.isOtpAutoLoginInProgress = true
        const payload = {
          otp_code,
          sub_domain,
          user_id,
          remember_me: remember_me === 'true',
        }

        this.loginHandler(payload)
        return
      }
    },

    handleGoogleLogin(payload) {
      this.isGoogleLogin = true
      this.loginWithOTPAndRedirect(payload).catch((err) => {})
    },
    handleMicrosoftLogin(payload) {
      this.isGoogleLogin = true
      this.loginWithOTPAndRedirect(payload)
    },
    async loginWithOTPAndRedirect(payload) {
      try {
        const { data } = await this.fetchOtpLoginData(payload)
        const otpPayload = {
          otp_code: data.otp_code,
          sub_domain: data.sub_domain,
          user_id: data.user_id,
          remember_me: payload.remember_me,
          provider: payload.provider || 'google',
        }

        const currentHost = window.location.hostname
        const hostParts = currentHost.split('.')
        const targetHost = [data.sub_domain, ...hostParts.slice(1)].join('.')

        if (currentHost !== targetHost) {
          const params = new URLSearchParams(otpPayload).toString()
          window.location.href = `https://${targetHost}/signin?${params}`
          return
        }
        this.loginHandler(otpPayload)
      } catch (err) {
        sessionStorage.removeItem('googleOAuthHandled')
        window.history.replaceState(null, '', window.location.pathname)
      }
    },
    redirectToSignup,
    checkUserRegistrationStatus: debounce(function (email) {
      this.userSubscriptionHandler(email)
    }, DELAY_FOR_CHECK_USER_REGISTRATION),
    countryChanged(payload) {
      this.loginData.phone = payload.formattedNumber
      this.phoneNumberValid = payload.isValid
    },
    switchLoginWith() {
      this.loginWithEmail = !this.loginWithEmail
      this.loginData.email = ''
      this.loginData.phone = ''
    },

    route() {
      var activeRole = this.$store.state.layout.activeRole
      let route = getDefaultRoute(activeRole, this.currentCampusScope, this.currentSectionScope)
      this.$router?.push({ name: route })
      if (this.userRoles.length > 1)
        this.$store.commit('layout/SET_SHOW_ROLE_SWITCHING_SUITE_MODAL', true)
    },
    /*
     * Use this method to make user login in tenant
     * Getting email,domain and password from user and check its valid or not
     * Getting invalid keyword as param to to check form data validation
     * If user is authentic then make user logged in and push him/her to his/her relevant route
     */
    async loginHandler(otpPayload) {
      this.isLoading = true
      removeEmptyKeysFromObject(this.loginData)

      if (!this.isGoogleLogin && this.loginData.email && this.loginData.phone) {
        this.$store.commit(
          'toast/NEW',
          { type: 'error', message: 'Either login with email or phone' },
          { root: true },
        )
        this.isLoading = false
        return
      }

      const payload = this.isGoogleLogin ? otpPayload : this.loginData

      try {
        if (!otpPayload?.otp_code) {
          try {
            this.otpData = await this.fetchOtpPayload(payload)
          } catch (err) {
            this.isLoading = false
            return
          }
        }

        const loginPayload = otpPayload?.otp_code ? otpPayload : this.otpData
        const domainType = currentDomainType(this.currentHostName)
        if (
          !isLocalHost() &&
          !isInHouseDevServers() &&
          (domainType === siteConstants.MYQAMPUS_PROD_MAIN_DOMAIN ||
            domainType === siteConstants.MYQAMPUS_BETA_DOMAIN)
        ) {
          const [, ...rest] = this.currentHostName.split('.')
          const targetHost = `${loginPayload.sub_domain}.${rest.join('.')}`

          if (this.currentHostName !== targetHost) {
            const params = new URLSearchParams({
              otp_code: loginPayload.otp_code,
              user_id: loginPayload.user_id,
              sub_domain: loginPayload.sub_domain,
              remember_me: loginPayload.remember_me,
            }).toString()
            const redirectUrl = `https://${targetHost}/signin?${params}`
            window.location.href = redirectUrl
            return
          }
        }

        const isOtpFlow = Boolean(otpPayload?.otp_code)
        // await this.tryLogInWithRetry(loginPayload, isOtpFlow)
        await this.logIn(loginPayload)
        this.initlizeTawkto()
        this.isLoading = false
        if (!isOtpFlow) {
          this.isOtpAutoLoginInProgress = false
        }
        var signUpModal = localStorage.getItem('signUpSlider')
        const authToken = localStorage.getItem('jwt') || null

        if (signUpModal) {
          this.$router?.push({ name: 'setup-slider' })
        } else {
          this.route()
          this.getSettings()
        }
        if (!this.$socket?.isConnected()) {
          this.$connectSocket('/tenant-notifire-socket', authToken)
        }
        this.setDefaultSettings() // Set System Settings
      } catch (error) {
        this.isLoading = false
        this.isOtpAutoLoginInProgress = false
        if (error.code === 401) this.errorMsg = error.message
      }
    },

    // async tryLogInWithRetry(loginPayload, isOtpFlow, attempt = 1) {
    //   try {
    //     await this.logIn(loginPayload)
    //   } catch (error) {
    //     const canRetry = isOtpFlow && attempt < 2

    //     if (canRetry) {
    //       this.otpLoginRetryCount += 1
    //       return this.tryLogInWithRetry(loginPayload, isOtpFlow, attempt + 1)
    //     }

    //     throw error
    //   }
    // },

    initlizeTawkto() {
      if (isTawkToAllowed()) {
        showTawkWidget()
        this.setTawkAttributes()
      }
    },

    async fetchOtpPayload(payload) {
      try {
        const res = await this.fetchOtpLoginData(payload)
        if (res?.data?.otp_code && res?.data?.user_id && res?.data?.sub_domain) {
          return {
            otp_code: res.data.otp_code,
            sub_domain: res.data.sub_domain,
            user_id: res.data.user_id,
            remember_me: payload.remember_me || false,
          }
        }
      } catch (error) {
        this.$store.commit(
          'toast/NEW',
          {
            type: 'error',
            message: error.message || 'Failed to get OTP. Please try again.',
          },
          { root: true },
        )
        throw error
      }
    },
    getSettings() {
      this.getFeaturesGeneralSettings().then((res) => {
        this.$store.commit('layout/SET_GENERAL_SETTINGS', objectDeepCopy(res))
      })
    },
    onInputUserEmail(event) {
      this.errorMsg = null
      this.isResetButton = false
      this.checkUserRegistrationStatus(event.target.value)
    },
    setDefaultSettings() {
      // Language
      let language = this.selectLang(
        this.systemSettings?.default_language?.default_language,
        'short',
      )
      this.saveLanguageToLocalStorage(language)

      // Theme
      let newTheme = this.systemSettings?.default_theme?.default_theme || 'green'
      if (newTheme == 'light') {
        newTheme = 'green'
      }
      this.saveSelectedTheme(newTheme)
      this.selectTheme(newTheme)
    },

    async notificationPermissionHandler() {
      await handleNotificationPermission({
        showToast: (options) => {
          this.$store.commit('toast/NEW', options, { root: true })
        },
        toastOptions: {
          message:
            'You have disabled the notification,if you want to get notification please enable it from info icon',
          type: 'info',
        },
      })
    },
    async domainHandler() {
      this.isDomainSelectionActive = siteDomainHandler().isDomainFieldVisible
      this.loginData.sub_domain = siteDomainHandler().domain
      if (this.loginData.sub_domain) {
        let payload = this.loginData.sub_domain.includes('.com')
          ? { site_url: this.loginData.sub_domain }
          : { sub_domain: this.loginData.sub_domain }
        this.$store.commit('layout/IS_LOADING', true)
        if (!this.$route.query.setup) {
          await this.getPublicSettings(payload)
        }
        this.$store.commit('layout/IS_LOADING', false)
      }
      this.setInstituteName()
    },
    setInstituteName() {
      this.instituteName = localStorage.getItem('generalSettingSiteTitle') || DOMAIN_TITLE
    },

    userSubscriptionHandler(email) {
      if (!isValidEmail(email)) return

      this.getUserRegistrationStatus({ email })
        .then(() => {
          this.errorMsg = null
          this.isResetButton = false
          this.getUserDomain(email)
        })
        .catch((error) => {
          this.userDomains = []
          this.errorMsg = error.message || 'Error checking user registration status'
          this.isResetButton = true
        })
    },
    async getUserDomain(email) {
      const response = await this.fetchUserDomains({ email })
      if (response?.sub_domains) this.userDomains = response.sub_domains
    },

    resendRegistrationEmailHandler() {
      this.resendRegistrationEmail({ email: this.loginData.email }).then(() => {
        this.$store.commit(
          'toast/NEW',
          { type: 'success', message: 'Registration email resent successfully.' },
          { root: true },
        )
      })
    },
    updateSetupStatusFromPayload(payload, defaultStatus = null) {
      const { status, message } = getTenantSetupStatusFromPayload(
        payload,
        defaultStatus,
        this.tenantSetupMessage,
      )
      if (status) {
        this.tenantSetupStatus = status
      }
      if (message) {
        this.tenantSetupMessage = message
      }
    },
    connectTenantSetupSocket() {
      const isLocalhost = isLocalHost()
      const shouldConnect = shouldConnectTenantSetupSocket({
        isOnTenantDomain: this.isOnTenantDomain,
        tenantOtp: this.tenantOtp,
        tenantSetup: this.tenantSetup,
        isLocalhost,
        tenantSubDomain: this.tenantSubDomain,
      })
      if (!shouldConnect) {
        return
      }
      this.tenantSetupStatus = 'connecting'
      this.tenantSetupMessage = 'Connecting...'

      const socketHandlers = getTenantSetupSocketHandlers({
        updateSetupStatusFromPayload: (payload, defaultStatus) => {
          this.updateSetupStatusFromPayload(payload, defaultStatus)
        },
        showSuccessToast: () => {
          this.$store.commit(
            'toast/NEW',
            {
              type: 'success',
              message: 'Your institute setup is complete! You can now sign in.',
            },
            { root: true },
          )
        },
        clearStatus: () => {
          this.tenantSetupStatus = null
          this.tenantSetupMessage = ''
        },
        clearQueryParams: () => {
          const query = { ...this.$route.query }
          delete query.otp
          delete query.email
          delete query.setup
          delete query.token
          this.$router.replace({ query })
        },
        setQueuedStatus: () => {
          this.tenantSetupStatus = 'queued'
          this.tenantSetupMessage = "We're getting everything ready for you..."
        },
      })

      this.$connectPublicSocket(this.tenantOtp, this.tenantEmail, socketHandlers)
    },

    ...mapActions('auth', [
      'logIn',
      'fetchOtpLoginData',
      'fetchUserDomains',
      'getUserRegistrationStatus',
      'resendRegistrationEmail',
    ]),
    ...mapActions('settings', ['getPublicSettings', 'getFeaturesGeneralSettings']),
  },
}
</script>

<style lang="scss">
@import './auth.scss';
</style>
